import React from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { num, pct } from "./format";

export const LEVEL_ORDER = ["state", "region", "zone", "client"];
const LEVEL_LABEL = { state: "State", region: "Region", zone: "Zone", client: "Location" };

const PctBar = ({ value, threshold }) => {
  const tone = value < threshold ? (value < threshold - 10 ? "red" : "amber") : "green";
  return (
    <div className="a2-pctbar">
      <div className="a2-meter">
        <span className={`fill-${tone}`} style={{ width: `${Math.min(100, value)}%` }} />
      </div>
      <b className={`t-${tone}`}>{pct(value)}</b>
    </div>
  );
};

// Manpower is set per shift; "Not set" until someone enters it for the shifts in this row
export const Manpower = ({ value, notSet }) => {
  if (value === null || value === undefined) return <span className="t-grey" title="Not available with the Designation filter or a search">—</span>;
  if (!value) return <span className="t-grey">Not set</span>;
  return (
    <>
      {num(value)}
      {notSet > 0 && <div className="a2-small" title="Locations whose shifts have no manpower yet">{num(notSet)} not set</div>}
    </>
  );
};

const BreakdownTable = ({ data, loading, level, onLevel, onDrill }) => {
  const rows = data?.rows || [];
  const total = data?.total;
  const threshold = data?.threshold ?? 80;
  const label = LEVEL_LABEL[level];
  const canDrill = level !== "client";

  return (
    <div className={`a2-card a2-flush a2-section ${loading ? "a2-loading" : ""}`}>
      <div className="a2-card-head">
        <div>
          <h3 className="a2-card-title">Workforce by {label}</h3>
          <div className="a2-card-sub">
            {canDrill ? `Select a ${label.toLowerCase()} to drill down. ` : "Select a location to see its employees. "}
            Attendance = present ÷ total manpower (employees where manpower is not set). Rows under {threshold}% are flagged.
          </div>
        </div>
        <div className="a2-levels a2-noprint">
          {LEVEL_ORDER.map((l, i) => (
            <React.Fragment key={l}>
              {i > 0 && <i>›</i>}
              <button className={l === level ? "active" : ""} onClick={() => onLevel(l)}>
                {LEVEL_LABEL[l]}
              </button>
            </React.Fragment>
          ))}
        </div>
      </div>
      <div className="a2-table-wrap">
        <table className="a2-table">
          <thead>
            <tr>
              <th>{label}</th>
              <th className="num">Employees</th>
              <th className="num">Total Manpower</th>
              <th className="num">Present</th>
              <th className="num">Absent</th>
              <th className="num">Leave</th>
              <th className="num">Punched In</th>
              <th className="num">Not Punched</th>
              <th>Attendance</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr
                key={r.id || "none"}
                className={`clickable ${r.needs_attention ? "a2-attention" : ""}`}
                onClick={() => r.id && onDrill(r)}
              >
                <td>
                  <div className="a2-name">{r.name}</div>
                  {r.needs_attention && <div className="a2-small t-red">Needs attention</div>}
                </td>
                <td className="num">{num(r.employees)}</td>
                <td className="num"><Manpower value={r.total_manpower} notSet={r.manpower_not_set} /></td>
                <td className="num t-green">{num(r.present + r.working)}</td>
                <td className="num t-red">{num(r.absent)}</td>
                <td className="num t-amber">{num(r.leave)}</td>
                <td className="num t-blue">{num(r.punched_in)}</td>
                <td className="num">{num(r.not_punched_in)}</td>
                <td>
                  <PctBar value={r.attendance_pct} threshold={threshold} />
                </td>
                <td className="a2-noprint">
                  <ChevronRightIcon fontSize="small" className="t-grey" />
                </td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={10} className="a2-empty">No attendance data for these filters.</td>
              </tr>
            )}
          </tbody>
          {total && rows.length > 0 && (
            <tfoot>
              <tr>
                <td>Total</td>
                <td className="num">{num(total.employees)}</td>
                <td className="num"><Manpower value={total.total_manpower} notSet={total.manpower_not_set} /></td>
                <td className="num">{num(total.present + total.working)}</td>
                <td className="num">{num(total.absent)}</td>
                <td className="num">{num(total.leave)}</td>
                <td className="num">{num(total.punched_in)}</td>
                <td className="num">{num(total.not_punched_in)}</td>
                <td>{pct(total.attendance_pct)}</td>
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default BreakdownTable;
